import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function TermsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Terms of Service - VisionSet',
    description: 'Read VisionSet\'s Terms of Service. Learn about user accounts, credits, billing, refunds, image licensing, DMCA compliance, and acceptable use policies.',
    keywords: 'terms of service, user agreement, legal terms, VisionSet terms',
    canonical: '/terms',
    ogTitle: 'Terms of Service - VisionSet',
    ogDescription: 'Read VisionSet\'s Terms of Service. Learn about user accounts, credits, billing, refunds, image licensing, DMCA compliance, and acceptable use policies.',
    ogUrl: '/terms',
    twitterTitle: 'Terms of Service - VisionSet',
    twitterDescription: 'Read VisionSet\'s Terms of Service.',
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Terms of Service",
      "url": `${siteUrl}/terms`,
      "description": "VisionSet Terms of Service - Legal terms and conditions for using the VisionSet platform.",
      "publisher": {
        "@type": "Organization",
        "name": "Ausonio Algorithmics LLC",
        "url": "https://www.ausonioalg.com/"
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="https://static.readdy.ai/image/4126e18b1195bdf88076d12182861244/e0a391f1061e2e4527f1f332a6fc5bcf.png" alt="VisionScout" className="h-10" />
          </Link>
          
          <Link to="/" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-5xl font-bold text-slate-900 mb-4">Terms of Service</h1>
        <p className="text-slate-600 mb-12">Last updated: January 2026</p>

        <div className="space-y-10 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">1. Company Information</h2>
            <p className="mb-4">
              VisionScout is operated by <strong>Ausonio Algorithmics LLC</strong>, a limited liability company organized under the laws of the State of Colorado, United States.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Company Name:</strong> Ausonio Algorithmics LLC</li>
              <li><strong>State of Organization:</strong> Colorado</li>
              <li><strong>Contact Email:</strong> support@VisionScout.com</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">2. Acceptance of Terms</h2>
            <p className="mb-4">
              By accessing and using VisionScout ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these Terms of Service, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">3. Description of Service</h2>
            <p className="mb-4">
              VisionScout provides a platform for creating computer vision datasets through image search, curation, and export functionality. The Service allows users to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Search for images using natural language descriptions</li>
              <li>Curate images through a swipe-based interface</li>
              <li>Export curated images as downloadable ZIP files</li>
              <li>Manage multiple dataset projects</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">4. User Accounts</h2>
            <p className="mb-4">
              To use certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain the security of your password and account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Be responsible for all activities that occur under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">5. Credits and Billing</h2>
            <p className="mb-4">
              VisionScout operates on a credit-based system where 1 credit equals 1 image delivered to your swipe pool.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Credits are consumed when images are delivered to your swipe pool</li>
              <li>Credits are not refundable once consumed</li>
              <li>Subscription plans renew automatically unless cancelled</li>
              <li>You may cancel your subscription at any time</li>
              <li>Unused credits may expire based on your plan tier</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">6. Payments and Refunds</h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">6.1 No Refunds Policy</h3>
            <p className="mb-4">
              All payments are final and non-refundable, including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Subscription fees for any billing period (monthly or annual)</li>
              <li>Credits purchased or included in subscription plans</li>
              <li>Unused credits at the time of cancellation</li>
              <li>Partial subscription periods</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">6.2 Chargebacks and Fraud</h3>
            <p className="mb-4">
              Initiating a chargeback or payment dispute without first contacting us may result in:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Immediate suspension or termination of your account</li>
              <li>Loss of access to all data and projects</li>
              <li>Forfeiture of all remaining credits</li>
              <li>Legal action to recover costs and damages</li>
            </ul>
            <p className="mt-4">
              We reserve the right to investigate and take appropriate action against fraudulent payment activity.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">6.3 Price Changes</h3>
            <p className="mb-4">
              We reserve the right to modify our pricing at any time. Price changes will:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Be communicated via email at least 30 days before taking effect</li>
              <li>Not affect your current billing period</li>
              <li>Apply to renewals after the notice period</li>
              <li>Give you the option to cancel before the new pricing takes effect</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">7. Image Licensing, Copyright, and DMCA</h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">7.1 No Rights Granted</h3>
            <p className="mb-4">
              VisionScout does not host, own, sell, or grant any rights to the images accessible through the Service. You are solely responsible for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Ensuring you have the right to use exported images for your intended purpose</li>
              <li>Complying with applicable copyright laws and licensing requirements</li>
              <li>Obtaining necessary permissions for commercial use</li>
              <li>Verifying the licensing status of images before use</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">7.2 Temporary Storage and Caching</h3>
            <p className="mb-4">
              We may temporarily store or cache image thumbnails and metadata to provide the Service. This temporary storage:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Is limited to the duration necessary to provide the Service</li>
              <li>Does not constitute hosting or distribution of copyrighted material</li>
              <li>May be deleted at any time according to our retention policies</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">7.3 Prohibited Redistribution</h3>
            <p className="mb-4">
              You may not use the Service to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Redistribute, resell, or sublicense images to third parties</li>
              <li>Create competing image search or dataset services</li>
              <li>Scrape or access restricted, private, or password-protected sources</li>
              <li>Bypass technological protection measures or access controls</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">7.4 DMCA Compliance</h3>
            <p className="mb-4">
              We respect intellectual property rights and respond to valid DMCA takedown notices. If you believe content accessible through our Service infringes your copyright:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Send a written notice to our designated DMCA agent at: dmca@VisionScout.com</li>
              <li>Include all information required under 17 U.S.C. § 512(c)(3)</li>
              <li>We will investigate and remove or disable access to allegedly infringing content</li>
            </ul>
            <p className="mt-4">
              We reserve the right to terminate accounts of repeat infringers in accordance with the DMCA.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">8. Content Removal and Repeat Infringer Policy</h2>
            <p className="mb-4">
              We reserve the right to remove any content and terminate access for users who:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Repeatedly infringe intellectual property rights</li>
              <li>Violate these Terms of Service</li>
              <li>Engage in abusive or fraudulent behavior</li>
              <li>Use the Service for illegal purposes</li>
            </ul>
            <p className="mt-4">
              Termination for repeat infringement may occur without prior notice and will result in permanent loss of account access and all associated data.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">9. Acceptable Use Policy</h2>
            <p className="mb-4">
              You agree not to use the Service to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the intellectual property rights of others</li>
              <li>Create datasets containing child sexual abuse material (CSAM) or any content involving minors in sexual or exploitative contexts</li>
              <li>Engage in doxxing, harassment, or invasion of privacy</li>
              <li>Distribute malware, viruses, or other harmful code</li>
              <li>Conduct unlawful surveillance or facial recognition without consent</li>
              <li>Scrape protected, restricted, or private content</li>
              <li>Create datasets for illegal, harmful, or discriminatory purposes</li>
              <li>Attempt to circumvent credit usage or billing systems</li>
              <li>Reverse engineer or attempt to extract the source code of the Service</li>
              <li>Use automated systems to abuse or overload the Service</li>
            </ul>
            <p className="mt-4">
              Violation of this Acceptable Use Policy will result in immediate account termination and may be reported to law enforcement.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">10. Service Limits and Fair Use</h2>
            <p className="mb-4">
              To ensure fair access and prevent abuse, we implement the following limits:
            </p>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">10.1 Rate Limits</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Maximum number of search requests per minute</li>
              <li>Maximum number of images that can be processed simultaneously</li>
              <li>Maximum export size and frequency</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">10.2 Resource Limits</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Maximum concurrent projects based on your plan</li>
              <li>Maximum storage allocation per account</li>
              <li>Maximum API calls per billing period</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">10.3 Throttling and Enforcement</h3>
            <p className="mb-4">
              We reserve the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Throttle or temporarily suspend accounts exceeding fair use limits</li>
              <li>Adjust limits based on system capacity and abuse patterns</li>
              <li>Terminate accounts engaged in systematic abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">11. Data Retention and Account Deletion</h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">11.1 Retention Periods</h3>
            <p className="mb-4">
              Image retention periods vary by plan:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Free plan: Images expire after 7 days</li>
              <li>Starter plan: Images expire after 1 month</li>
              <li>Pro plan: Images never expire (while account is active)</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">11.2 Account Deletion</h3>
            <p className="mb-4">
              When you delete your account:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>All projects and curated images are permanently deleted within 30 days</li>
              <li>Previously exported ZIP files remain available for download for 7 days</li>
              <li>Account information is anonymized or deleted within 90 days</li>
              <li>We retain transaction records and credit ledgers for 7 years for tax and auditing purposes</li>
              <li>Anonymized usage analytics may be retained indefinitely</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">11.3 Data Export</h3>
            <p className="mb-4">
              You may export your data at any time through:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Project export functionality (ZIP downloads)</li>
              <li>Account data export request (contact support)</li>
            </ul>
            <p className="mt-4">
              We do not provide exports of cached thumbnails or temporary data.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">12. Service Modifications</h2>
            <p className="mb-4">
              We reserve the right to modify or discontinue the Service at any time, with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuance of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">13. Limitation of Liability</h2>
            <p className="mb-4">
              To the maximum extent permitted by law, VisionScout and Ausonio Algorithmics LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Your use or inability to use the Service</li>
              <li>Any unauthorized access to or use of our servers</li>
              <li>Any bugs, viruses, or other harmful code transmitted through the Service</li>
              <li>Any errors or omissions in any content</li>
              <li>Any copyright infringement claims arising from your use of exported images</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">14. Disclaimer of Warranties</h2>
            <p className="mb-4">
              The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">15. Termination</h2>
            <p className="mb-4">
              We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms of Service.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">16. Governing Law and Dispute Resolution</h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">16.1 Governing Law</h3>
            <p className="mb-4">
              These Terms of Service shall be governed by and construed in accordance with the laws of the State of Colorado, United States, without regard to its conflict of law provisions.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">16.2 Venue</h3>
            <p className="mb-4">
              Any legal action or proceeding arising out of or relating to these Terms or the Service shall be brought exclusively in the state or federal courts located in Colorado, and you consent to the personal jurisdiction of such courts.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">16.3 Arbitration Agreement (Consumer Users)</h3>
            <p className="mb-4">
              For consumer users (individuals using the Service for personal, non-commercial purposes), any dispute, claim, or controversy arising out of or relating to these Terms or the Service shall be resolved by binding arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules, except as follows:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You may bring claims in small claims court if they qualify</li>
              <li>Either party may seek injunctive relief in court for intellectual property infringement</li>
              <li>The arbitration shall take place in Colorado</li>
              <li>The arbitrator's decision shall be final and binding</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">16.4 Class Action Waiver</h3>
            <p className="mb-4">
              You agree that any arbitration or legal proceeding shall be conducted on an individual basis and not as a class action, consolidated action, or representative action. You waive any right to participate in a class action lawsuit or class-wide arbitration.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">16.5 Opt-Out Right</h3>
            <p className="mb-4">
              You may opt out of the arbitration agreement by sending written notice to support@VisionScout.com within 30 days of first accepting these Terms. If you opt out, the arbitration provisions will not apply to you, but all other terms will remain in effect.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">17. Changes to Terms</h2>
            <p className="mb-4">
              We reserve the right to modify these terms at any time. We will notify users of any material changes by posting the new Terms of Service on this page and updating the "Last updated" date. For significant changes, we will also send email notification to registered users.
            </p>
            <p className="mt-4">
              Your continued use of the Service after changes become effective constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">18. Contact Information</h2>
            <p className="mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Email:</strong> support@VisionScout.com</li>
              <li><strong>DMCA Notices:</strong> dmca@VisionScout.com</li>
              <li><strong>Company:</strong> Ausonio Algorithmics LLC</li>
              <li><strong>State:</strong> Colorado, United States</li>
            </ul>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-200">
          <Link to="/" className="text-blue-600 font-medium hover:text-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line"></i> Back to Home
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12 mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-slate-400">
            <p>&copy; 2025 VisionScout. All rights reserved.</p>
            <p>Built by <a href="https://www.ausonioalg.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Ausonio Algorithmics</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
